import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import type { Theme } from "types";

const ClockContainer = styled.div<{ theme?: Theme }>`
  position: fixed;
  bottom: 20px;
  left: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 12px 16px;
  border-radius: 16px;
  background-color: ${(props) => props.theme?.body || "#ffffff"};
  border: 2px solid ${(props) => props.theme?.accentColor || "#6366f1"};
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  cursor: pointer;
  user-select: none;
  transition: all 0.3s ease;

  /* Decorative flourish only — on phones it overlaps the hero CTAs and the
     footer, so keep it to tablets and up where the bottom-left corner is free. */
  @media (max-width: 767px) {
    display: none;
  }

  &:hover {
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.25);
    border-color: ${(props) => props.theme?.accentBright || "#8b5cf6"};
  }

  &:active {
    transform: translateY(-1px) scale(0.98);
  }

  &:focus-visible {
    outline: 3px solid ${(props) => props.theme?.accentBright || "#8b5cf6"};
    outline-offset: 4px;
  }
`;

const TimeContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const TimeText = styled.div<{ theme?: Theme }>`
  font-size: 18px;
  font-weight: 800;
  font-family: "SF Mono", "Monaco", "Inconsolata", "Roboto Mono", monospace;
  line-height: 1.2;
  color: ${(props) => props.theme?.text || "#000"};
`;

const DateText = styled.div<{ theme?: Theme }>`
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => props.theme?.secondaryText || "#666"};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  pointer-events: ${(props) => (props.isOpen ? "auto" : "none")};
  transition: opacity 0.3s ease;
  cursor: pointer;
`;

const ModalContent = styled.div<{ theme?: Theme }>`
  position: relative;
  background: ${(props) => props.theme?.body || "#ffffff"};
  border-radius: 30px;
  padding: 50px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.4);
  border: 3px solid ${(props) => props.theme?.accentColor || "#6366f1"};
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;

  @media (max-width: 768px) {
    padding: 30px;
    gap: 20px;
  }
`;

const CloseButton = styled.button<{ theme?: Theme }>`
  position: absolute;
  top: 20px;
  right: 20px;
  background: ${(props) => props.theme?.accentColor || "#6366f1"};
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => props.theme?.accentBright || "#8b5cf6"};
    transform: rotate(90deg);
  }
`;

const AnalogClock = styled.svg<{ theme?: Theme }>`
  width: 280px;
  height: 280px;
  filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.2));

  @media (max-width: 768px) {
    width: 240px;
    height: 240px;
  }
`;

const DigitalTime = styled.div<{ theme?: Theme }>`
  font-size: 36px;
  font-weight: 900;
  font-family: "SF Mono", "Monaco", "Inconsolata", "Roboto Mono", monospace;
  color: ${(props) => props.theme?.text || "#000"};
  text-align: center;
  letter-spacing: 2px;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const DigitalDate = styled.div<{ theme?: Theme }>`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => props.theme?.secondaryText || "#666"};
  text-transform: uppercase;
  letter-spacing: 1px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

interface LiveClockProps {
  theme?: Theme;
}

const LiveClock: React.FC<LiveClockProps> = ({ theme }) => {
  const [time, setTime] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const clockRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const hourHandRef = useRef<SVGLineElement>(null);
  const minuteHandRef = useRef<SVGLineElement>(null);
  const secondHandRef = useRef<SVGGElement>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000); // Update every second for smooth animation

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (clockRef.current) {
      gsap.from(clockRef.current, { opacity: 0, y: 20, duration: 1 });
    }
  }, []);

  useEffect(() => {
    if (isModalOpen && modalRef.current) {
      gsap.fromTo(
        modalRef.current,
        { scale: 0.8, opacity: 0, rotation: -10 },
        {
          scale: 1,
          opacity: 1,
          rotation: 0,
          duration: 0.5,
          ease: "back.out(1.7)",
        },
      );
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (
      isModalOpen &&
      hourHandRef.current &&
      minuteHandRef.current &&
      secondHandRef.current
    ) {
      const hours = time.getHours() % 12;
      const minutes = time.getMinutes();
      const seconds = time.getSeconds();

      const secondAngle = seconds * 6;
      const minuteAngle = (minutes + seconds / 60) * 6;
      const hourAngle = hours * 30 + minutes / 2;

      hourHandRef.current.setAttribute(
        "transform",
        `rotate(${hourAngle} 200 200)`,
      );
      minuteHandRef.current.setAttribute(
        "transform",
        `rotate(${minuteAngle} 200 200)`,
      );
      secondHandRef.current.setAttribute(
        "transform",
        `rotate(${secondAngle} 200 200)`,
      );
    }
  }, [time, isModalOpen]);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatFullTime = (date: Date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatFullDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleCloseModal = useCallback(() => {
    if (!isModalOpen) return;

    if (modalRef.current) {
      gsap.to(modalRef.current, {
        scale: 0.8,
        opacity: 0,
        rotation: 10,
        duration: 0.3,
        ease: "back.in(1.7)",
        onComplete: () => setIsModalOpen(false),
      });
    }
  }, [isModalOpen]);

  const openModal = useCallback(() => {
    if (!isModalOpen) {
      setIsModalOpen(true);
    }
  }, [isModalOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCloseModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleCloseModal]);

  return (
    <>
      <ClockContainer
        ref={clockRef}
        theme={theme}
        role="button"
        tabIndex={0}
        aria-label="Open large clock"
        onClick={openModal}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openModal();
          }
        }}
      >
        <TimeContainer>
          <TimeText theme={theme}>{formatTime(time)}</TimeText>
          <DateText theme={theme}>{formatDate(time)}</DateText>
        </TimeContainer>
      </ClockContainer>

      <ModalOverlay isOpen={isModalOpen} onClick={handleCloseModal}>
        <ModalContent
          ref={modalRef}
          theme={theme}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton theme={theme} onClick={handleCloseModal}>
            ×
          </CloseButton>

          <AnalogClock viewBox="0 0 400 400">
            <defs>
              <radialGradient id="clockFaceGradient" cx="50%" cy="45%" r="65%">
                <stop
                  offset="0%"
                  stopColor={theme?.body || "#ffffff"}
                  stopOpacity="1"
                />
                <stop
                  offset="60%"
                  stopColor={theme?.body || "#ffffff"}
                  stopOpacity="0.9"
                />
                <stop
                  offset="100%"
                  stopColor={theme?.accentColor || "#6366f1"}
                  stopOpacity="0.1"
                />
              </radialGradient>
            </defs>

            {/* Outer Ring Shadow */}
            <circle
              cx="200"
              cy="200"
              r="195"
              fill="none"
              stroke="rgba(0,0,0,0.1)"
              strokeWidth="2"
            />

            {/* Clock Face */}
            <circle
              cx="200"
              cy="200"
              r="190"
              fill="url(#clockFaceGradient)"
              stroke={theme?.accentColor || "#6366f1"}
              strokeWidth="6"
            />

            {/* Inner decorative circle */}
            <circle
              cx="200"
              cy="200"
              r="185"
              fill="none"
              stroke={theme?.accentColor || "#6366f1"}
              strokeWidth="1"
              opacity="0.3"
            />

            {/* Hour Markers */}
            {[...Array(12)].map((_, i) => {
              const angle = (i * 30 * Math.PI) / 180;
              const isMainMarker = i % 3 === 0;
              const x1 = 200 + Math.sin(angle) * (isMainMarker ? 155 : 165);
              const y1 = 200 - Math.cos(angle) * (isMainMarker ? 155 : 165);
              const x2 = 200 + Math.sin(angle) * 175;
              const y2 = 200 - Math.cos(angle) * 175;
              return (
                <line
                  key={i}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={theme?.text || "#000"}
                  strokeWidth={isMainMarker ? "5" : "3"}
                  strokeLinecap="round"
                  opacity={isMainMarker ? "1" : "0.6"}
                />
              );
            })}

            {/* Minute Markers */}
            {[...Array(60)].map((_, i) => {
              const angle = (i * 6 * Math.PI) / 180;
              const x1 = 200 + Math.sin(angle) * 172;
              const y1 = 200 - Math.cos(angle) * 172;
              const x2 = 200 + Math.sin(angle) * 178;
              const y2 = 200 - Math.cos(angle) * 178;
              return (
                <line
                  key={`minor-${i}`}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={theme?.text || "#000"}
                  strokeWidth="1"
                  opacity={i % 5 === 0 ? 0 : 0.3}
                />
              );
            })}

            {/* Hour Numbers */}
            {[...Array(12)].map((_, i) => {
              const angle = (i * 30 * Math.PI) / 180;
              const x = 200 + Math.sin(angle) * 130;
              const y = 200 - Math.cos(angle) * 130;
              return (
                <text
                  key={i}
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={theme?.text || "#000"}
                  fontSize="22"
                  fontWeight="700"
                  fontFamily="'SF Pro Display', 'Segoe UI', Arial, sans-serif"
                >
                  {i === 0 ? 12 : i}
                </text>
              );
            })}

            {/* Hour Hand */}
            <line
              ref={hourHandRef}
              x1="200"
              y1="200"
              x2="200"
              y2="122"
              stroke={theme?.text || "#1f2937"}
              strokeWidth="10"
              strokeLinecap="round"
            />

            {/* Minute Hand */}
            <line
              ref={minuteHandRef}
              x1="200"
              y1="200"
              x2="200"
              y2="68"
              stroke={theme?.accentColor || "#6366f1"}
              strokeWidth="6"
              strokeLinecap="round"
              style={{
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.25))",
              }}
            />

            {/* Second Hand - Composite group with counterweight and luminous tip */}
            <g
              ref={secondHandRef}
              style={{
                filter: "drop-shadow(0 1px 2px rgba(239,68,68,0.5))",
              }}
            >
              {/* Main hand */}
              <line
                x1="200"
                y1="200"
                x2="200"
                y2="50"
                stroke="#ef4444"
                strokeWidth="2"
                strokeLinecap="round"
              />

              {/* Tip embellishment */}
              <circle cx="200" cy="50" r="4" fill="#ef4444" />
              <circle cx="200" cy="50" r="2" fill="#fde68a" />
            </g>

            {/* Center Assembly */}
            <circle
              cx="200"
              cy="200"
              r="18"
              fill={theme?.accentColor || "#6366f1"}
              filter="drop-shadow(0 2px 8px rgba(0,0,0,0.3))"
            />
            <circle cx="200" cy="200" r="12" fill={theme?.body || "#ffffff"} />
            <circle
              cx="200"
              cy="200"
              r="6"
              fill={theme?.accentColor || "#6366f1"}
            />
          </AnalogClock>

          <DigitalTime theme={theme}>{formatFullTime(time)}</DigitalTime>
          <DigitalDate theme={theme}>{formatFullDate(time)}</DigitalDate>
        </ModalContent>
      </ModalOverlay>
    </>
  );
};

export default LiveClock;
